import { type ReactNode } from "react";
import { Hint, Input, TextArea } from "../ui/Field";
import { Tag } from "../ui/Tag";
import { AddLocale } from "./AddLocale";
import { BASE_LOCALE, localeOf } from "../../lib/locales";
import { useT } from "../../lib/i18n";

export interface I18nGridField {
  key: string;
  label: ReactNode;
  hint?: string;
  /** 基准语言这一格是否必填，默认 true */
  required?: boolean;
  /**
   * 长文本（个人介绍这类）用多行输入框。
   *
   * 刻意做成本组件的一个选项，而不是另起一个 I18nTextArea：
   * 那等于把抽屉、语言增删、回退提示整套复制一份，两套行为迟早漂移。
   */
  multiline?: boolean;
}

/**
 * 多语言编辑：**所有语言同屏平铺**，一行一种语言，一列一个字段。
 *
 * 以前每个页面各写一版「语言标签页」：点一下切一种，想知道哪几种没填只能一个个点过去。
 * 平铺以后缺口一眼可见，而且城市名 + 主建筑这种成对的字段能横向对照着填。
 *
 * 语言不是写死的清单：行尾可以删、底部可以从目录里加——真要发的语言各产品线不一样，
 * 写死就意味着每接一个区改一次代码。
 */
export function I18nGrid({
  locales, fields, value, onChange, editable = true, onAddLocale, onRemoveLocale, note,
}: {
  locales: string[];
  fields: I18nGridField[];
  value: (localeId: string, fieldKey: string) => string;
  onChange: (localeId: string, fieldKey: string, v: string) => void;
  editable?: boolean;
  onAddLocale: (id: string) => void;
  onRemoveLocale: (id: string) => void;
  note?: ReactNode;
}) {
  const t = useT();
  // 待回填 = 任一必填字段为空。只看第一列的话，日语填了城市名漏了主建筑，
  // 汇总仍显示「已回填」，缺项要到发布才炸出来
  const required = fields.filter((f) => f.required !== false);
  const missing = locales.filter((id) => id !== BASE_LOCALE && required.some((f) => !value(id, f.key).trim()));

  return (
    <div>
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col className="w-[132px]" />
          {fields.map((f) => <col key={f.key} />)}
          <col className="w-[56px]" />
        </colgroup>
        <thead>
          <tr>
            <th className="h-[34px] whitespace-nowrap border-b border-line-lighter bg-fill-light px-2 text-left text-sm font-semibold text-ink-regular">
              {t("i18n.langCol")}
            </th>
            {fields.map((f) => (
              <th key={f.key} className="h-[34px] whitespace-nowrap border-b border-line-lighter bg-fill-light px-2 text-left text-sm font-semibold text-ink-regular">
                <span className="inline-flex items-center">{f.label}{f.hint && <Hint text={f.hint} />}</span>
              </th>
            ))}
            <th className="h-[34px] border-b border-line-lighter bg-fill-light px-2 text-center text-sm font-semibold text-ink-regular">
              {t("cost.col.op")}
            </th>
          </tr>
        </thead>
        <tbody>
          {locales.map((id) => {
            const l = localeOf(id);
            const isBase = id === BASE_LOCALE;
            return (
              <tr key={id} className={isBase ? "bg-primary-50" : undefined}>
                <td className="border-b border-line-lighter px-2 py-1 text-sm text-ink">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-[30px] text-xs text-ink-placeholder">{l.short}</span>
                    <span className={isBase ? "font-semibold" : undefined}>{l.label}</span>
                    {isBase && <Tag tone="primary">{t("i18n.base")}</Tag>}
                  </span>
                </td>
                {fields.map((f) => {
                  const baseText = value(BASE_LOCALE, f.key);
                  // 非基准语言的占位符直接显示基准语言的值：留空会回退成它，
                  // 与其写「留空回退默认语言」不如把回退后的字摆出来
                  const placeholder = isBase
                    ? f.required === false ? "" : t("i18n.required")
                    : baseText ? t("i18n.fallbackTo", { v: baseText }) : t("i18n.fallback");
                  return (
                    <td key={f.key} className="border-b border-line-lighter px-2 py-1 align-top">
                      {f.multiline ? (
                        <TextArea
                          value={value(id, f.key)}
                          disabled={!editable}
                          placeholder={placeholder}
                          onChange={(v) => onChange(id, f.key, v)}
                        />
                      ) : (
                        <Input
                          value={value(id, f.key)}
                          disabled={!editable}
                          placeholder={placeholder}
                          onChange={(v) => onChange(id, f.key, v)}
                        />
                      )}
                    </td>
                  );
                })}
                <td className="border-b border-line-lighter px-2 py-1 text-center">
                  {isBase ? (
                    <span className="text-xs text-ink-placeholder" title={t("i18n.baseUndeletable")}>—</span>
                  ) : (
                    <button
                      type="button"
                      title={t("i18n.removeLang", { lang: l.label })}
                      disabled={!editable}
                      onClick={() => onRemoveLocale(id)}
                      className="text-sm leading-none text-ink-placeholder hover:text-danger disabled:cursor-not-allowed"
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
        <AddLocale locales={locales} onAdd={onAddLocale} disabled={!editable} />
        <span className="text-xs text-ink-placeholder">
          {missing.length
            ? t("i18n.pending", { n: locales.length, m: missing.length, langs: missing.map((x) => localeOf(x).short).join("、") })
            : t("i18n.complete", { n: locales.length })}
          {note && <> · {note}</>}
        </span>
      </div>
    </div>
  );
}
